import { useState } from 'react';
import type { PendingReview } from '@teentack/shared';
import { Button } from '@/components/ui/Button';
import { ErrorNote } from '@/components/Form';
import { presentApiError } from '@/lib/form-errors';
import { StarPicker } from './Stars';
import { useCreateReview, usePendingReviews } from './use-reviews';

/**
 * "How did that go?" [Job Poster step 29, Parent step 21]
 *
 * Reviews only get written if someone is asked, so this sits on the console
 * rather than behind a settings page. It disappears on its own once there is
 * nothing left to rate.
 */

function PromptCard({ item }: { item: PendingReview }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const create = useCreateReview();

  async function submit(): Promise<void> {
    if (rating === 0) {
      setError('Pick a rating from 1 to 5');
      return;
    }

    setError(null);
    try {
      await create.mutateAsync({
        assignmentId: item.assignmentId,
        rating,
        ...(comment.trim() ? { comment: comment.trim() } : {}),
      });
      setDone(true);
    } catch (caught) {
      setError(presentApiError(caught, 'Could not save that review').message);
    }
  }

  if (done) {
    return (
      <li className="rounded-2xl border border-safe-200 bg-safe-50 p-5">
        <p className="text-sm font-semibold text-safe-700">
          Thanks — your review of {item.subjectName} is saved.
        </p>
      </li>
    );
  }

  return (
    <li className="rounded-2xl border border-line bg-white p-5 shadow-soft">
      <p className="text-sm font-bold text-ink">How did {item.subjectName} do?</p>
      <p className="mt-0.5 text-xs text-slate-500">{item.jobTitle}</p>

      <div className="mt-3">
        <StarPicker value={rating} onChange={setRating} disabled={create.isPending} />
      </div>

      <label htmlFor={`comment-${item.assignmentId}`} className="sr-only">
        Add a comment
      </label>
      <textarea
        id={`comment-${item.assignmentId}`}
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        rows={2}
        maxLength={2000}
        placeholder="Add a note (optional)"
        className="mt-3 w-full resize-none rounded-xl border border-line bg-white px-3.5 py-2.5 text-sm text-ink outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
      />

      {error && (
        <div className="mt-2">
          <ErrorNote message={error} />
        </div>
      )}

      <div className="mt-3">
        <Button disabled={create.isPending} onClick={() => void submit()}>
          {create.isPending ? 'Saving…' : 'Leave review'}
        </Button>
      </div>
    </li>
  );
}

/** Renders nothing at all when there is nothing to review. */
export function ReviewPrompt({ enabled = true }: { enabled?: boolean }) {
  const pending = usePendingReviews(enabled);
  const items = pending.data ?? [];

  if (items.length === 0) return null;

  return (
    <section>
      <h2 className="mb-4 text-base font-bold text-ink">
        {items.length === 1 ? 'One job to review' : `${items.length} jobs to review`}
      </h2>
      <ul className="space-y-3">
        {items.map((item) => (
          <PromptCard key={item.assignmentId} item={item} />
        ))}
      </ul>
    </section>
  );
}
